import {
  ChangeDetectionStrategy, Component, ElementRef, NgZone, OnDestroy, computed, effect, inject, input, output, signal, untracked, viewChild,
} from '@angular/core';
import { initials } from '../../../core/utils/format.util';
import { tintFor } from '../../../core/utils/color.util';
import { environment } from '../../../../environments/environment';
import { GoogleMapsLoaderService } from '../../../core/services/google-maps-loader.service';
import { ToastService } from '../../../core/services/toast.service';

type LatLng = google.maps.LatLngLiteral;
type MapState = 'loading' | 'ready' | 'no-key' | 'error';

const DEFAULT_CENTER: LatLng = { lat: 19.4326, lng: -99.1332 };
/**
 * Autocomplete (New) sin negocios: máx. 5 tipos primarios de Tabla A/B (o solo '(regions)'/'(cities)', que no se combinan).
 * 'geocode' no sirve: el filtro es por tipo primario y una dirección tiene primario street_address, no geocode.
 * Dirección exacta, calle, colonia, ciudad/municipio y estado. Fuente: developers.google.com/maps/documentation/places/web-service/place-types
 */
const SEARCH_TYPES = ['street_address', 'route', 'sublocality_level_1', 'locality', 'administrative_area_level_1'];
/** Tipos que son una dirección exacta (ahí sí se coloca el pin). Calle, colonia, ciudad o estado solo acercan el mapa. */
const EXACT_TYPES = new Set(['street_address', 'premise', 'subpremise']);
const PIN_SVG = `<svg viewBox="0 0 34 44" aria-hidden="true"><path d="M17 44C17 44 34 28.2 34 17A17 17 0 0 0 0 17C0 28.2 17 44 17 44Z" fill="#0A5C5A" stroke="#fff" stroke-width="2" stroke-linejoin="round"/></svg>`;

/**
 * Mapa de Google (modo claro) con el pin del negocio: gota con la punta en la coordenada exacta y el logo/iniciales dentro.
 * Editable: arrastrar el pin, tocar el mapa, «Usar mi ubicación» o elegir una dirección exacta en el buscador actualizan lat/lng.
 * La ubicación es opcional (lat/lng nulos = sin pin). Sin `googleMapsApiKey` muestra un aviso en lugar del mapa.
 */
@Component({
  selector: 'app-location-map',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (state()) {
      @case ('ready') {
        @if (editable()) {
          <div #search class="search"></div>
          <button #locateBtn type="button" class="locate" [class.is-busy]="locating()" [attr.aria-busy]="locating()"
                  aria-label="Usar mi ubicación" title="Usar mi ubicación" (click)="locate()"><cr-icon name="locate" [size]="22" /></button>
          @if (!hasPin()) { <p class="hint"><cr-icon name="pin" [size]="16" /> Toca el mapa para poner el pin</p> }
        }
        <google-map class="gmap" width="100%" height="100%" [options]="options"
                    (mapInitialized)="onMap($event)" (mapClick)="onMapClick($event)" (authFailure)="onAuthFailure()">
          @if (pos(); as p) {
            <map-advanced-marker [position]="p" [content]="pin()" [gmpDraggable]="editable()" [title]="name() || 'Tu negocio'"
                                 (mapDragend)="onDragEnd($event)" />
          }
        </google-map>
      }
      @default {
        @if (state() !== 'loading') {
          <p class="notice"><cr-icon name="info" [size]="16" />{{ state() === 'no-key' ? 'Falta la llave de Google Maps' : 'No se pudo cargar Google Maps' }}</p>
        }
        @if (editable()) {
          <button type="button" class="locate locate--float" [class.is-busy]="locating()" [attr.aria-busy]="locating()"
                  aria-label="Usar mi ubicación" title="Usar mi ubicación" (click)="locate()"><cr-icon name="locate" [size]="22" /></button>
        }
      }
    }
  `,
  styles: [`
    :host{display:block;position:relative;overflow:hidden;border-radius:inherit;min-height:180px;background:var(--cr-map-street)}
    .gmap{position:absolute;inset:0;z-index:0;display:block}
    .search{position:absolute;top:12px;right:12px;z-index:2;width:min(360px,calc(100% - 24px))}
    .locate{width:52px;height:52px;margin:0 12px 12px 0;border-radius:50%;border:1px solid var(--cr-border-subtle);
      background:#fff;box-shadow:var(--cr-elevation-2);display:grid;place-items:center;cursor:pointer;color:var(--cr-text-primary);
      transition:opacity var(--cr-motion-quick)}
    .locate:focus-visible{outline:2px solid var(--cr-focus-ring);outline-offset:2px}
    .locate.is-busy{opacity:.55;cursor:progress}
    .locate--float{position:absolute;right:16px;bottom:16px;z-index:2;margin:0}
    .hint{position:absolute;left:50%;top:68px;transform:translateX(-50%);z-index:1;margin:0;display:flex;gap:6px;align-items:center;pointer-events:none;
      padding:8px 14px;border-radius:999px;background:var(--cr-bg-inverse);color:#fff;font:600 13px/18px var(--cr-font-body);white-space:nowrap}
    .notice{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);margin:0;display:flex;gap:6px;align-items:center;max-width:calc(100% - 32px);
      padding:8px 14px;border-radius:999px;background:var(--cr-bg-surface);color:var(--cr-text-secondary);box-shadow:var(--cr-elevation-1);
      font:600 13px/18px var(--cr-font-body);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  `],
})
export class LocationMapComponent implements OnDestroy {
  readonly lat = input<number | null | undefined>(null);
  readonly lng = input<number | null | undefined>(null);
  readonly name = input<string>('');
  readonly logoUrl = input<string | null | undefined>(null);
  readonly editable = input<boolean>(false);
  readonly zoom = input<number>(16);
  readonly pinChange = output<{ lat: number; lng: number }>();

  private loader = inject(GoogleMapsLoaderService);
  private toast = inject(ToastService);
  private zone = inject(NgZone);

  readonly state = signal<MapState>(this.loader.hasKey ? 'loading' : 'no-key');
  readonly locating = signal(false);
  readonly pos = computed<LatLng | null>(() => {
    const lat = this.lat();
    const lng = this.lng();
    return typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng) ? { lat, lng } : null;
  });
  readonly hasPin = computed(() => this.pos() !== null);
  readonly pin = computed(() => buildPin(this.name() || 'Tu negocio', this.logoUrl()));

  options: google.maps.MapOptions = {};
  private map = signal<google.maps.Map | null>(null);
  private searchHost = viewChild<ElementRef<HTMLDivElement>>('search');
  private locateBtn = viewChild<ElementRef<HTMLButtonElement>>('locateBtn');
  private autocomplete?: google.maps.places.PlaceAutocompleteElement;
  private controlsReady = false;
  private prevPos: LatLng | null = null;
  private lastEmit: LatLng | null = null;
  private destroyed = false;

  constructor() {
    if (this.loader.hasKey) {
      this.loader.load().then(ok => {
        if (this.destroyed) return;
        if (!ok) { this.state.set('error'); return; }
        this.options = this.buildOptions();
        this.state.set('ready');
      });
    }
    // Pin ↔ mapa: centra al aparecer desde fuera; si ya existía, solo lo mantiene a la vista.
    effect(() => {
      const map = this.map();
      const p = this.pos();
      if (map) untracked(() => this.follow(map, p));
    });
    // Controles sobre el mapa (buscador y «Usar mi ubicación») una vez que el mapa existe.
    effect(() => {
      const map = this.map();
      const search = this.searchHost();
      const btn = this.locateBtn();
      if (map && search && btn && !this.controlsReady) untracked(() => this.setupControls(map, search.nativeElement, btn.nativeElement));
    });
  }

  // region map
  private buildOptions(): google.maps.MapOptions {
    const p = this.pos();
    return {
      mapId: environment.googleMapsMapId || 'DEMO_MAP_ID',
      center: p ?? DEFAULT_CENTER,
      zoom: p ? this.zoom() : 12,
      colorScheme: 'LIGHT',
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
      clickableIcons: false,
      gestureHandling: this.editable() ? 'greedy' : 'cooperative',
    };
  }

  onMap(map: google.maps.Map): void {
    this.zone.run(() => this.map.set(map));
  }

  onAuthFailure(): void {
    this.zone.run(() => {
      this.map.set(null);
      this.state.set('error');
    });
  }

  private setupControls(map: google.maps.Map, searchHost: HTMLElement, locateBtn: HTMLButtonElement): void {
    this.controlsReady = true;
    map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(locateBtn);
    const ac = new google.maps.places.PlaceAutocompleteElement({
      includedRegionCodes: ['mx'],
      includedPrimaryTypes: SEARCH_TYPES,
      requestedLanguage: 'es',
      requestedRegion: 'mx',
      placeholder: 'Busca una ciudad, colonia o dirección',
    });
    ac.classList.add('cr-gmap-search');
    ac.addEventListener('gmp-select', ev => void this.selectPlace(ev.placePrediction));
    searchHost.appendChild(ac);
    this.autocomplete = ac;
  }

  private follow(map: google.maps.Map, p: LatLng | null): void {
    if (!p) { this.prevPos = null; return; }
    const self = !!this.lastEmit && same(p, this.lastEmit);
    if (!this.prevPos && !self) {
      map.setCenter(p);
      map.setZoom(this.zoom());
    } else if (!insideInner(map, p)) {
      map.panTo(p);
    }
    this.prevPos = p;
  }

  // region search
  private async selectPlace(prediction: google.maps.places.PlacePrediction): Promise<void> {
    const place = prediction.toPlace();
    try {
      await place.fetchFields({ fields: ['location', 'viewport', 'types'] });
    } catch {
      this.zone.run(() => this.toast.error('No pudimos abrir ese lugar'));
      return;
    }
    this.zone.run(() => this.goToPlace(place));
  }

  private goToPlace(place: google.maps.places.Place): void {
    const map = this.map();
    const loc = place.location;
    if (!map || (!place.viewport && !loc)) return;
    const exact = !!loc && (place.types ?? []).some(t => EXACT_TYPES.has(t));
    if (place.viewport) {
      map.fitBounds(place.viewport);
      if (exact) google.maps.event.addListenerOnce(map, 'idle', () => { if ((map.getZoom() ?? 0) > 18) map.setZoom(18); });
    } else if (loc) {
      map.setCenter(loc);
      map.setZoom(exact ? 17 : 14);
    }
    if (exact && loc) this.emit(loc.lat(), loc.lng());
  }

  // region pin
  onMapClick(e: google.maps.MapMouseEvent | google.maps.IconMouseEvent): void {
    if (this.editable() && e.latLng) this.emit(e.latLng.lat(), e.latLng.lng());
  }

  onDragEnd(e: google.maps.MapMouseEvent): void {
    if (e.latLng) this.emit(e.latLng.lat(), e.latLng.lng());
  }

  private emit(lat: number, lng: number): void {
    const r = (n: number) => Math.round(n * 1e6) / 1e6;
    this.lastEmit = { lat: r(lat), lng: r(lng) };
    this.pinChange.emit(this.lastEmit);
  }

  // region locate
  locate(): void {
    if (this.locating()) return;
    if (!('geolocation' in navigator)) { this.toast.error('Tu navegador no permite obtener tu ubicación'); return; }
    if (!window.isSecureContext) { this.toast.error('La ubicación solo funciona con HTTPS'); return; }
    this.locating.set(true);
    navigator.geolocation.getCurrentPosition(
      pos => this.zone.run(() => {
        this.locating.set(false);
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        this.emit(p.lat, p.lng);
        const map = this.map();
        if (map) { map.setCenter(p); map.setZoom(17); }
      }),
      err => this.zone.run(() => {
        this.locating.set(false);
        this.toast.error(err.code === err.PERMISSION_DENIED ? 'Permiso de ubicación denegado' : 'No pudimos obtener tu ubicación');
      }),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.autocomplete?.remove();
  }
}

/** Pin en gota (34×44, la punta inferior es la coordenada) con logo o iniciales y etiqueta con el nombre debajo de la punta. */
function buildPin(name: string, logoUrl: string | null | undefined): HTMLElement {
  const root = document.createElement('div');
  root.className = 'cr-gmap-pin';
  root.innerHTML = PIN_SVG;
  const face = document.createElement('span');
  face.className = 'cr-gmap-pin__face';
  if (logoUrl) {
    const img = document.createElement('img');
    img.src = logoUrl;
    img.alt = '';
    face.appendChild(img);
  } else {
    const t = tintFor(name);
    face.style.background = t.bg;
    face.style.color = t.ink;
    face.textContent = initials(name);
  }
  const label = document.createElement('span');
  label.className = 'cr-gmap-pin__label';
  label.textContent = name;
  root.append(face, label);
  return root;
}

function same(a: LatLng, b: LatLng): boolean {
  return Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lng - b.lng) < 1e-6;
}

/** ¿El punto está dentro del 60 % central de lo visible? */
function insideInner(map: google.maps.Map, p: LatLng): boolean {
  const b = map.getBounds();
  if (!b) return false;
  const ne = b.getNorthEast();
  const sw = b.getSouthWest();
  const dLat = (ne.lat() - sw.lat()) * 0.2;
  const dLng = (ne.lng() - sw.lng()) * 0.2;
  return p.lat < ne.lat() - dLat && p.lat > sw.lat() + dLat && p.lng < ne.lng() - dLng && p.lng > sw.lng() + dLng;
}
